import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlayerProfile } from '../../../../shared/types/game';
import { Socket } from 'socket.io-client';
import styles from './MyPage.module.css';

interface MyPageProps {
  playerId: string;
  playerName: string;
  onNameUpdate: (newName: string) => void;
  socket: Socket | null;
  isRegistered: boolean;
  onLogout: () => void;
  isSocketConnected: boolean;
  isPlayerConnectedToServer: boolean;
}

const MyPage: React.FC<MyPageProps> = ({ playerId, playerName, onNameUpdate, socket, isRegistered, onLogout, isSocketConnected, isPlayerConnectedToServer }) => {
  const [profile, setProfile] = useState<PlayerProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [newName, setNewName] = useState<string>(playerName);
  const [nameUpdateMessage, setNameUpdateMessage] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const handlePlayerProfile = (data: PlayerProfile | null) => {
      setLoading(false);
      if (data) {
        setProfile(data);
        localStorage.setItem('shogiPlayerName', data.name);
        onNameUpdate(data.name);
        setNewName(data.name);
        setError(null);
      } else {
        setError('プレイヤープロフィールが見つかりませんでした。');
      }
    };

    if (socket) {
      socket.on('player:profile', handlePlayerProfile);
    }

    const fetchProfile = () => {
      if (socket && playerId && isSocketConnected && isPlayerConnectedToServer) {
        setLoading(true);
        setError(null);
        console.log(`MyPage: プロフィール取得リクエストを送信します。PlayerID: ${playerId}`);
        socket.emit('player:getProfile', { playerId }, handlePlayerProfile);
      } else if (!isSocketConnected || !isPlayerConnectedToServer) {
        setLoading(true);
        setError('サーバー接続を待っています...');
        console.log('MyPage: ソケット未接続またはプレイヤー未接続のためプロフィール取得をスキップ。', { socket, isSocketConnected, isPlayerConnectedToServer });
      } else {
        setLoading(false);
        setError('プレイヤーIDがありません。');
        console.warn('MyPage: プレイヤーIDがないためプロフィール取得をスキップ。');
      }
    };

    if (playerId) {
      fetchProfile();
    } else {
      setLoading(false);
      setError('プレイヤーIDがありません。');
      console.warn('MyPage: 初期ロード時にプレイヤーIDが不足しています。');
    }

    return () => {
      if (socket) {
        socket.off('player:profile', handlePlayerProfile);
      }
    };
  }, [playerId, onNameUpdate, socket, isSocketConnected, isPlayerConnectedToServer]);

  const handleNameChange = useCallback(async () => {
    setNameUpdateMessage(null);

    // 登録済みユーザーでない場合は、ここで処理を停止し、メッセージを表示
    if (!isRegistered) {
      setNameUpdateMessage('プレイヤー名の変更はアカウント登録済みのユーザー限定の機能です。');
      setIsEditingName(false); // 編集モードを終了
      setNewName(profile?.name || playerName); // 入力値を元の名前に戻す
      return;
    }

    if (newName.trim() === '') {
      setNameUpdateMessage('プレイヤー名を入力してください。');
      return;
    }

    if (newName.trim() === profile?.name) {
      setNameUpdateMessage('変更がありません。');
      setIsEditingName(false);
      return;
    }

    if (!socket || !isSocketConnected || !isPlayerConnectedToServer) {
      setNameUpdateMessage('サーバーに接続していません。しばらく待ってから再度お試しください。');
      return;
    }

    if (!playerId) {
      setNameUpdateMessage('プレイヤーIDがありません。');
      return;
    }

    console.log(`MyPage: プレイヤー名更新リクエストを送信します。ID: ${playerId}, 新しい名前: ${newName.trim()}`);
    socket.emit('player:updateName', { playerId, newName: newName.trim() }, (response: { success: boolean; message?: string; updatedName?: string }) => {
      if (response.success) {
        setNameUpdateMessage('プレイヤー名を更新しました！');
        setIsEditingName(false);
        if (socket && playerId && isSocketConnected && isPlayerConnectedToServer) {
             socket.emit('player:getProfile', { playerId }, (data: PlayerProfile | null) => {
              if (data) {
                setProfile(data);
                localStorage.setItem('shogiPlayerName', data.name);
                onNameUpdate(data.name);
              } else {
                setNameUpdateMessage('更新後のプロフィール取得に失敗しました。');
              }
            });
          }
      } else {
        let specificMessage = response.message || '名前の更新に失敗しました。';
        if (specificMessage.includes('既に存在する') || specificMessage.includes('Duplicate key')) {
          specificMessage = `このプレイヤー名「${newName.trim()}」は既に使われています。別の名前をお試しください。`;
        }
        setNameUpdateMessage(specificMessage);
        console.error('MyPage: プレイヤー名更新失敗:', response.message);
      }
    });
  }, [isRegistered, playerId, newName, onNameUpdate, profile?.name, playerName, socket, isSocketConnected, isPlayerConnectedToServer]);


  const calculateWinRate = (wins: number, losses: number) => {
    if (wins + losses === 0) return '0%';
    return `${((wins / (wins + losses)) * 100).toFixed(1)}%`;
  };

  const handleLogoutClick = () => {
    onLogout();
  };

  if (loading && (!profile || error === 'サーバー接続を待っています...')) {
    return (
      <div className={styles.container}>
        <p className={styles.loadingText}>プロフィールを読み込み中...</p>
      </div>
    );
  }

  if (error && error !== 'サーバー接続を待っています...') {
    return (
      <div className={styles.container}>
        <p className={styles.errorText}>エラー: {error}</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={styles.container}>
        <p className={styles.infoText}>プロフィール情報がありません。</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.h2}>マイページ</h2>
      <div className={styles.profileSection}>
        <div className={styles.profileItem}>
          <strong className={styles.label}>プレイヤー名:</strong>
          {isEditingName && isRegistered ? (
            <div className={styles.editNameContainer}>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className={styles.nameInput}
                disabled={!isSocketConnected || !isPlayerConnectedToServer}
                maxLength={20}
                placeholder="新しいプレイヤー名"
              />
              <button onClick={handleNameChange} className={styles.saveButton} disabled={!isSocketConnected || !isPlayerConnectedToServer || newName.trim() === ''}>保存</button>
              <button onClick={() => {
                setIsEditingName(false);
                setNewName(profile.name);
                setNameUpdateMessage(null);
              }} className={styles.cancelButton}>キャンセル</button>
            </div>
          ) : (
            <div className={styles.displayNameContainer}>
              <span className={styles.value}>{profile.name}</span>
              {isRegistered ? (
                <button onClick={() => setIsEditingName(true)} className={styles.editButton}>変更</button>
              ) : (
                <span className={styles.guestInfo}> (ゲスト)</span>
              )}
            </div>
          )}
        </div>
        {(nameUpdateMessage || (!isRegistered && !isEditingName)) && (
          <p className={`${styles.message} ${!isRegistered && !isEditingName ? styles.infoMessage : ''}`}>
            {nameUpdateMessage || 'プレイヤー名の変更はアカウント登録済みのユーザー限定の機能です。'}
          </p>
        )}


        <div className={styles.profileItem}>
          <strong className={styles.label}>レート:</strong>
          <span className={styles.value}>{profile.currentRating.toFixed(0)}</span>
        </div>
        <div className={styles.profileItem}>
          <strong className={styles.label}>最高レート:</strong>
          <span className={styles.value}>{profile.highestRating.toFixed(0)}</span>
        </div>
        <div className={styles.profileItem}>
          <strong className={styles.label}>勝利数:</strong>
          <span className={styles.value}>{profile.wins}</span>
        </div>
        <div className={styles.profileItem}>
          <strong className={styles.label}>敗北数:</strong>
          <span className={styles.value}>{profile.losses}</span>
        </div>
        <div className={styles.profileItem}>
          <strong className={styles.label}>勝率:</strong>
          <span className={styles.value}>{calculateWinRate(profile.wins, profile.losses)}</span>
        </div>
        <div className={styles.profileItem}>
          <strong className={styles.label}>現在の連勝数:</strong>
          <span className={styles.value}>{profile.currentWinStreak}</span>
        </div>
        <div className={styles.profileItem}>
          <strong className={styles.label}>最高連勝数:</strong>
          <span className={styles.value}>{profile.highestWinStreak}</span>
        </div>
        <div className={styles.profileItem}>
          <strong className={styles.label}>登録日:</strong>
          <span className={styles.value}>{new Date(profile.createdAt).toLocaleDateString()}</span>
          {!isRegistered && <span className={styles.guestInfo}> (ゲスト)</span>}
        </div>
        <div className={styles.profileItem}>
          <strong className={styles.label}>最終ログイン:</strong>
          <span className={styles.value}>
            {new Date(profile.lastLoginAt).toLocaleString('ja-JP', {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      </div>
      <div className={styles.buttonGroup}>
        <button onClick={() => navigate('/matching')} className={styles.backButton}>マッチング画面に戻る</button>
        {isRegistered && (
          <button onClick={handleLogoutClick} className={styles.logoutButton}>ログアウト</button>
        )}
      </div>
    </div>
  );
};

export default MyPage;